import React, { useMemo, useState } from 'react'
import {
  Box, Card, CardContent, Grid, Typography, Button, ButtonGroup,
  Chip, Tooltip, Collapse, IconButton
} from '@mui/material'
import { format, subDays, parseISO, startOfISOWeek, getISODay } from 'date-fns'

import { Layout } from '_templates'
import { ReportsApi } from '_services'
import CraneDashboardChart from '../components/organisms/CraneDashboardChart'

const { useGetCraneDashboardQuery } = ReportsApi

// ─── Constants ───
const PLAN_CLASSIFICATION_MAP = {
  planned_before_day: { code: 'D', label: 'Day Before (>6h)', color: '#1B5E20', score: 3 },
  planned_early:      { code: 'B', label: 'Before 8am (3–6h)', color: '#4CAF50', score: 2 },
  mixed:              { code: 'M', label: 'Mixed', color: '#FF9800', score: 1.5 },
  planned_late:       { code: 'A', label: 'Late (<3h)', color: '#F44336', score: 1 },
  no_plan:            { code: '',  label: 'No Plan', color: '#9E9E9E', score: 0 },
}

const OPS_COLOR = (hours) => {
  if (hours === 0) return '#263238'
  if (hours <= 2) return '#64B5F6'
  if (hours <= 4) return '#2196F3'
  return '#1565C0'
}

const OPS_LABEL = (hours) => {
  if (hours === 0) return 'No Logging'
  return `${hours.toFixed(1)}h — ${hours <= 2 ? 'Low' : hours <= 4 ? 'Medium' : 'High'}`
}

const STATUS_MAP = {
  active: { label: 'Active', color: '#4CAF50' },
  complete: { label: 'Complete', color: '#FF9800' },
  'not-started': { label: 'Not Started', color: '#9E9E9E' },
}

// ─── Data Processing Helpers ───
function buildSitesFromCraneDays(craneDays) {
  const siteMap = {}

  craneDays.forEach((cd) => {
    if (!siteMap[cd.projectId]) {
      siteMap[cd.projectId] = {
        id: cd.projectId,
        name: cd.projectName,
        cranes: {},
      }
    }
    const site = siteMap[cd.projectId]
    if (!site.cranes[cd.plantId]) {
      site.cranes[cd.plantId] = {
        id: cd.plantId,
        name: cd.plantName,
        champion: cd.plannerLabel || null,
        days: {},
      }
    }
    site.cranes[cd.plantId].days[cd.date] = cd
  })

  return Object.values(siteMap).map((site) => ({
    ...site,
    cranes: Object.values(site.cranes),
  }))
}

function getSiteStatus(site) {
  const allDays = site.cranes.flatMap((c) => Object.values(c.days))
  if (!allDays.some((d) => d.engagementHours > 0)) return 'not-started'
  const sortedDates = [...new Set(allDays.map((d) => d.date))].sort()
  const recent20Dates = sortedDates.slice(-20)
  const recentDays = allDays.filter((d) => recent20Dates.includes(d.date))
  if (!recentDays.some((d) => d.engagementHours > 0)) return 'complete'
  return 'active'
}

function cranePlanPct(crane, recentN) {
  const dates = Object.keys(crane.days).sort()
  const slice = recentN ? dates.slice(-recentN) : dates
  if (!slice.length) return 0
  const total = slice.reduce((acc, d) => {
    const cd = crane.days[d]
    const info = PLAN_CLASSIFICATION_MAP[cd.planningClassification] || PLAN_CLASSIFICATION_MAP.no_plan
    return acc + info.score
  }, 0)
  return Math.round((total / (slice.length * 3)) * 100)
}

function craneOpsPct(crane, recentN) {
  const dates = Object.keys(crane.days).sort()
  const slice = recentN ? dates.slice(-recentN) : dates
  if (!slice.length) return 0
  const active = slice.filter((d) => crane.days[d].engagementHours > 0).length
  return Math.round((active / slice.length) * 100)
}

function craneEngPct(crane, recentN) {
  return Math.round((cranePlanPct(crane, recentN) + craneOpsPct(crane, recentN)) / 2)
}

// ─── Ring Gauge ───
function RingGauge({ pct, size = 64, color = '#2196F3', label }) {
  const r = (size - 8) / 2
  const circ = 2 * Math.PI * r
  const dash = (pct / 100) * circ

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e0e0e0" strokeWidth={6} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <Box sx={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: size > 56 ? 15 : 12, fontWeight: 700, color, lineHeight: 1 }}>
          {pct}%
        </Typography>
        {label && (
          <Typography sx={{ fontSize: 8, color: 'text.secondary', mt: '1px' }}>{label}</Typography>
        )}
      </Box>
    </Box>
  )
}

// ─── Bar ───
function Bar({ value, color, height = 6 }) {
  return (
    <Box sx={{ height, bgcolor: '#e0e0e0', borderRadius: height, overflow: 'hidden' }}>
      <Box sx={{
        height: '100%', width: `${value}%`, bgcolor: color,
        borderRadius: height, transition: 'width 0.6s ease',
      }} />
    </Box>
  )
}

// ─── KPI Card ───
function KPICard({ label, value, color, sub }) {
  return (
    <Card sx={{ borderTop: `3px solid ${color}` }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography sx={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mt: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{sub}</Typography>
      </CardContent>
    </Card>
  )
}

// ─── Site Card ───
function SiteCard({ site, recentN, onClick, selected }) {
  const status = getSiteStatus(site)
  const sp = Math.round(site.cranes.reduce((a, c) => a + cranePlanPct(c, recentN), 0) / site.cranes.length)
  const so = Math.round(site.cranes.reduce((a, c) => a + craneOpsPct(c, recentN), 0) / site.cranes.length)
  const eng = Math.round((sp + so) / 2)
  const engColor = eng >= 65 ? '#4CAF50' : eng >= 35 ? '#FF9800' : '#F44336'
  const champions = [...new Set(site.cranes.map((c) => c.champion).filter(Boolean))]

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        transition: 'all .15s',
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        '&:hover': { borderColor: selected ? 'primary.main' : 'text.disabled' },
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{site.name}</Typography>
            {champions.length > 0
              ? <Typography variant="caption" color="text.secondary">🏆 {champions.join(' · ')}</Typography>
              : <Typography variant="caption" color="text.disabled" fontStyle="italic">No champion assigned</Typography>
            }
            <Typography variant="caption" display="block" color="text.disabled">
              {site.cranes.length} crane{site.cranes.length > 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
            <Chip
              size="small"
              label={STATUS_MAP[status].label}
              sx={{
                bgcolor: `${STATUS_MAP[status].color}18`,
                border: `1px solid ${STATUS_MAP[status].color}40`,
                color: STATUS_MAP[status].color,
                fontWeight: 700,
                fontSize: 11,
              }}
            />
            <RingGauge pct={eng} size={52} color={engColor} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Planning timeliness</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ color: '#4CAF50' }}>{sp}%</Typography>
            </Box>
            <Bar value={sp} color="#4CAF50" />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Operator logging</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ color: '#2196F3' }}>{so}%</Typography>
            </Box>
            <Bar value={so} color="#2196F3" />
          </Box>
        </Box>

        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5, textAlign: 'right' }}>
          {selected ? '▲ Viewing cranes' : 'Click to drill down →'}
        </Typography>
      </CardContent>
    </Card>
  )
}

// ─── Heatmap Grid ───
function HeatmapGrid({ crane, type }) {
  const [hover, setHover] = useState(null)
  const dates = Object.keys(crane.days).sort()

  // Group by ISO week
  const weeks = {}
  dates.forEach((d) => {
    const weekStart = format(startOfISOWeek(parseISO(d)), 'dd MMM')
    if (!weeks[weekStart]) weeks[weekStart] = []
    weeks[weekStart].push(d)
  })

  const weekEntries = Object.entries(weeks)

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {type === 'plan' ? 'Planning Timeliness' : 'Operator Activity'}
      </Typography>
      <Box sx={{ display: 'flex', gap: '2px', mt: 0.5, flexWrap: 'wrap' }}>
        {weekEntries.map(([weekLabel, weekDates]) => (
          <Box key={weekLabel} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
            <Typography sx={{ fontSize: 8, color: 'text.disabled', mb: '2px' }}>{weekLabel}</Typography>
            <Box sx={{ display: 'flex', gap: '1px' }}>
              {weekDates.map((d) => {
                const cd = crane.days[d]
                const hk = `${type}-${d}`
                let bg, tooltipText

                if (type === 'plan') {
                  const info = PLAN_CLASSIFICATION_MAP[cd.planningClassification] || PLAN_CLASSIFICATION_MAP.no_plan
                  bg = info.color
                  tooltipText = `${format(parseISO(d), 'dd MMM')}: ${info.label}`
                } else {
                  bg = OPS_COLOR(cd.engagementHours)
                  tooltipText = `${format(parseISO(d), 'dd MMM')}: ${OPS_LABEL(cd.engagementHours)}`
                }

                return (
                  <Tooltip key={d} title={tooltipText} arrow>
                    <Box
                      onMouseEnter={() => setHover(hk)}
                      onMouseLeave={() => setHover(null)}
                      sx={{
                        width: 20, height: 20, bgcolor: bg, borderRadius: '2px',
                        opacity: hover && hover === hk ? 0.7 : 1,
                        outline: hover && hover === hk ? '1px solid white' : 'none',
                        cursor: 'default', transition: 'opacity .1s',
                      }}
                    />
                  </Tooltip>
                )
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// ─── Crane Row ───
function CraneRow({ crane, recentN, expanded, onToggle }) {
  const sp = cranePlanPct(crane, recentN)
  const so = craneOpsPct(crane, recentN)
  const eng = craneEngPct(crane, recentN)
  const engColor = eng >= 65 ? '#4CAF50' : eng >= 35 ? '#FF9800' : '#F44336'

  return (
    <Card sx={{ mb: 1, border: expanded ? '2px solid' : '1px solid', borderColor: expanded ? 'primary.main' : 'divider' }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box onClick={onToggle} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="span" fontWeight={700} fontSize={14}>{crane.name}</Typography>
            {crane.champion
              ? <Typography component="span" sx={{ ml: 1, fontSize: 12, color: 'text.secondary' }}>
                  Champion: {crane.champion}
                </Typography>
              : <Typography component="span" sx={{ ml: 1, fontSize: 11, color: 'text.disabled', fontStyle: 'italic' }}>
                  No champion
                </Typography>
            }
          </Box>
          <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#4CAF50', lineHeight: 1 }}>{sp}%</Typography>
              <Typography sx={{ fontSize: 9, color: 'text.secondary', mt: '2px' }}>PLANNING</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#2196F3', lineHeight: 1 }}>{so}%</Typography>
              <Typography sx={{ fontSize: 9, color: 'text.secondary', mt: '2px' }}>LOGGING</Typography>
            </Box>
            <RingGauge pct={eng} size={48} color={engColor} />
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            <HeatmapGrid crane={crane} type="plan" />
            <Box sx={{ mt: 1.5 }}>
              <HeatmapGrid crane={crane} type="ops" />
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}

// ─── Main Dashboard ───
const CraneDashboard = () => {
  const [selectedSiteId, setSelectedSiteId] = useState(null)
  const [expandedCrane, setExpandedCrane] = useState(null)
  const [period, setPeriod] = useState('all')
  const [dateRange] = useState({
    start: format(subDays(new Date(), 120), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  })

  const {
    data: dashboardData,
    isFetching: isLoading,
    error: dashboardError,
  } = useGetCraneDashboardQuery({
    startDate: dateRange.start,
    endDate: dateRange.end,
  })

  const sites = useMemo(() => {
    if (!dashboardData?.craneDays) return []
    return buildSitesFromCraneDays(dashboardData.craneDays)
  }, [dashboardData])

  const recentN = period === 'recent' ? 20 : null
  const selectedSite = sites.find((s) => s.id === selectedSiteId) || null
  const viewCranes = selectedSite ? selectedSite.cranes : sites.flatMap((s) => s.cranes)

  const overallPlan = viewCranes.length
    ? Math.round(viewCranes.reduce((a, c) => a + cranePlanPct(c, recentN), 0) / viewCranes.length)
    : 0
  const overallOps = viewCranes.length
    ? Math.round(viewCranes.reduce((a, c) => a + craneOpsPct(c, recentN), 0) / viewCranes.length)
    : 0
  const overallEng = Math.round((overallPlan + overallOps) / 2)
  const activeSites = sites.filter((s) => getSiteStatus(s) === 'active').length

  // Build weekly data for chart
  const weeklyData = useMemo(() => {
    if (!dashboardData?.craneDays) return []
    const craneDays = selectedSite
      ? dashboardData.craneDays.filter((cd) => cd.projectId === selectedSite.id)
      : dashboardData.craneDays

    const weekMap = {}
    craneDays.forEach((cd) => {
      const weekStart = format(startOfISOWeek(parseISO(cd.date)), 'dd MMM')
      if (!weekMap[weekStart]) weekMap[weekStart] = { planScores: [], opsActive: 0, opsTotal: 0 }
      const info = PLAN_CLASSIFICATION_MAP[cd.planningClassification] || PLAN_CLASSIFICATION_MAP.no_plan
      weekMap[weekStart].planScores.push(info.score)
      weekMap[weekStart].opsTotal++
      if (cd.engagementHours > 0) weekMap[weekStart].opsActive++
    })

    return Object.entries(weekMap).map(([week, data]) => ({
      week,
      plan: Math.round((data.planScores.reduce((a, b) => a + b, 0) / (data.planScores.length * 3)) * 100),
      ops: Math.round((data.opsActive / data.opsTotal) * 100),
    }))
  }, [dashboardData, selectedSite])

  // Determine date range for subtitle
  const dateRangeLabel = useMemo(() => {
    if (!dashboardData?.craneDays?.length) return ''
    const dates = dashboardData.craneDays.map((cd) => cd.date).sort()
    const first = format(parseISO(dates[0]), 'dd MMM')
    const last = format(parseISO(dates[dates.length - 1]), 'dd MMM yyyy')
    return `${first} – ${last}`
  }, [dashboardData])

  return (
    <Layout
      title="Crane Dashboard"
      isLoading={isLoading}
      maxWidth="false"
      productType="lnb"
    >
      {!isLoading && dashboardData && (
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
            <Box>
              {selectedSite && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setSelectedSiteId(null); setExpandedCrane(null) }}
                  sx={{ mb: 1.5 }}
                >
                  ← All Sites
                </Button>
              )}
              <Typography variant="h5" fontWeight={800}>
                {selectedSite ? selectedSite.name : 'LNB Crane Engagement Monitor'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedSite
                  ? `${selectedSite.cranes.length} cranes · `
                  : `${sites.length} sites · ${viewCranes.length} cranes · `
                }
                {dateRangeLabel}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Scoring period:</Typography>
              <ButtonGroup size="small">
                {[['all', 'All time'], ['recent', 'Last 4 wks']].map(([v, l]) => (
                  <Button
                    key={v}
                    onClick={() => setPeriod(v)}
                    variant={period === v ? 'contained' : 'outlined'}
                  >
                    {l}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>

          {/* KPIs */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <KPICard label="Planning Score" value={`${overallPlan}%`} color="#4CAF50" sub="Avg timeliness across cranes" />
            </Grid>
            <Grid item xs={6} md={3}>
              <KPICard label="Operator Logging" value={`${overallOps}%`} color="#2196F3" sub="Days with active tracking" />
            </Grid>
            <Grid item xs={6} md={3}>
              <KPICard
                label="Overall Engagement"
                value={`${overallEng}%`}
                color={overallEng >= 65 ? '#4CAF50' : overallEng >= 35 ? '#FF9800' : '#F44336'}
                sub="Combined plan + logging"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <KPICard label="Active Sites" value={`${activeSites} / ${sites.length}`} color="#7B1FA2" sub={`${viewCranes.length} cranes monitored`} />
            </Grid>
          </Grid>

          {/* Main Content */}
          {!selectedSite ? (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {sites.map((site) => (
                <Grid item xs={12} sm={6} md={4} key={site.id}>
                  <SiteCard
                    site={site}
                    recentN={recentN}
                    selected={selectedSiteId === site.id}
                    onClick={() => { setSelectedSiteId(site.id); setExpandedCrane(null) }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5, display: 'block' }}>
                Crane Detail — click to expand heatmap
              </Typography>
              {selectedSite.cranes.map((crane) => (
                <CraneRow
                  key={crane.id}
                  crane={crane}
                  recentN={recentN}
                  expanded={expandedCrane === crane.id}
                  onToggle={() => setExpandedCrane(expandedCrane === crane.id ? null : crane.id)}
                />
              ))}
            </Box>
          )}

          {/* Chart */}
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2, color: 'text.secondary' }}>
              Weekly Engagement Trend — {selectedSite ? selectedSite.name : 'All Sites'}
            </Typography>
            <CraneDashboardChart weeklyData={weeklyData} />
          </Card>

          {/* Legend */}
          <Card sx={{ p: 2 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1, display: 'block' }}>
                  Plan Timeliness
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {Object.entries(PLAN_CLASSIFICATION_MAP).map(([key, info]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: info.color, borderRadius: '2px' }} />
                      <Typography variant="caption" color="text.secondary">{info.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1, display: 'block' }}>
                  Operator Activity
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {[
                    ['#263238', 'No Logging'],
                    ['#64B5F6', 'Low (0–2h)'],
                    ['#2196F3', 'Medium (2–4h)'],
                    ['#1565C0', 'High (4h+)'],
                  ].map(([color, label]) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: '2px', border: '1px solid', borderColor: 'divider' }} />
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Box>
      )}

      {dashboardError && (
        <Card sx={{ p: 2, m: 2 }}>
          <Typography color="error">Failed to load crane dashboard data.</Typography>
        </Card>
      )}
    </Layout>
  )
}

export default CraneDashboard
